import type { HubGridRow, ProductFinanceRow } from '../../../../@types/oneButtonType';

export type OpportunityProductSavePayload = {
    opportunity_Id: number;
    product_Type_Name: string;
    product_Type_Id: number;
    finance_Partner_Id: number;
    activeFlag: boolean;
    opportunity_Product_Id: number;
    finance_Partner: string;
    finance_Partner_Display_Name: string;
    finance_Method_Name: string;
};

export function hubRowToOpportunityProductPayload(
    r: HubGridRow | ProductFinanceRow,
    opportunityId: number | string,
    productTypeList: { Text: string; Value?: string | number }[] = [],
): OpportunityProductSavePayload {
    const hasValidPartner =
        !!r.finance_Partner_Display_Name && r.finance_Partner_Display_Name !== 'Select Finance Partner';
    const partner = hasValidPartner ? String(r.finance_Partner_Display_Name) : '';

    return {
        opportunity_Id: +opportunityId,
        product_Type_Name: r.product_Type_Name,
        product_Type_Id:
            r.product_Type_Id || Number(productTypeList.find((x) => x.Text === r.product_Type_Name)?.Value) || 0,
        finance_Partner_Id: partner ? r.finance_Partner_Id : 0,
        activeFlag: !!r.activeFlag,
        opportunity_Product_Id: r.opportunity_Product_Id || 0,
        finance_Partner: partner ? r.finance_Partner || partner : '',
        finance_Partner_Display_Name: partner,
        finance_Method_Name: r.finance_Method_Name === 'Select Method' ? '' : r.finance_Method_Name || '',
    };
}

/** Build API payload; scoped save keeps persisted values for non-target products. */
export function buildOpportunityProductSavePayload(
    rows: HubGridRow[],
    originalArr: unknown[],
    opportunityId: number | string,
    productTypeList: { Text: string; Value?: string | number }[],
    scopeProductName?: string,
): OpportunityProductSavePayload[] {
    const savedList = Array.isArray(originalArr) ? originalArr : [];

    const shouldInclude = (r: HubGridRow) => {
        if (scopeProductName) {
            if (r.product_Type_Name === scopeProductName) return true;
            return savedList.some((op) => matchProductName(op, r.product_Type_Name));
        }
        return r.activeFlag || savedList.some((op) => matchProductName(op, r.product_Type_Name));
    };

    return rows.filter(shouldInclude).map((r) => {
        if (scopeProductName && r.product_Type_Name !== scopeProductName) {
            const saved = savedList.find((op) => matchProductName(op, r.product_Type_Name));
            if (saved) {
                const fromSaved = rowFromApi(saved, r.product_Type_Name);
                const activeFlag =
                    (saved as { ActiveFlag?: boolean }).ActiveFlag !== false &&
                    (saved as { activeFlag?: boolean }).activeFlag !== false;
                return hubRowToOpportunityProductPayload(
                    { ...fromSaved, activeFlag },
                    opportunityId,
                    productTypeList,
                );
            }
            return hubRowToOpportunityProductPayload({ ...r, activeFlag: false }, opportunityId, productTypeList);
        }
        return hubRowToOpportunityProductPayload(r, opportunityId, productTypeList);
    });
}

export function validateProductFinanceForSave(
    rows: HubGridRow[],
    scopeProductName?: string,
): string | null {
    if (scopeProductName) {
        const row = rows.find((r) => r.product_Type_Name === scopeProductName);
        if (!row) return 'Product not found.';
        if (!row.activeFlag) return `Enable ${scopeProductName} before saving.`;
        if (!row.finance_Partner_Display_Name || row.finance_Partner_Display_Name === 'Select Finance Partner') {
            return `Select a finance partner for ${scopeProductName}.`;
        }
        return null;
    }

    const activeRows = rows.filter((r) => r.activeFlag);
    if (!activeRows.length) return 'Please select at least one product.';

    const missingPartners = activeRows.filter(
        (r) => !r.finance_Partner_Display_Name || r.finance_Partner_Display_Name === 'Select Finance Partner',
    );
    if (missingPartners.length) {
        return `Select a finance partner for: ${missingPartners.map((r) => r.product_Type_Name).join(', ')}`;
    }
    return null;
}

export const PRODUCT_DISPLAY_ORDER = ['Solar', 'Roof', 'Battery', 'RR', 'RRR'];

export const isRoofFamily = (product: string) => product === 'Roof' || product === 'RR' || product === 'RRR';

export const isLightreach = (p: string) => /light\s*reach/i.test(p);
export const isSunnova = (p: string) => /sunnova/i.test(p);
export const isGoodLeap = (p: string) => /good\s*leap/i.test(p);

export function roofProductCodeForC4(product: string): string {
    if (product === 'RR') return 'RR';
    if (product === 'RRR') return 'RRR';
    return 'R';
}

export function partnerKeyForSyncModal(display: string): string {
    const x = String(display || '').toLowerCase();
    if (x.includes('light')) return 'LightReach';
    if (x.includes('good') && x.includes('leap')) return 'GoodLeap';
    if (x.includes('igs')) return 'IGS';
    if (x.includes('sunnova')) return 'IGS';
    if (x.includes('service')) return 'GoodLeap';
    if (x.includes('cash')) return 'Cash';
    return 'GoodLeap';
}

export function rowFromApi(p: any | undefined, productType: string): ProductFinanceRow {
    if (!p) {
        return {
            product_Type_Name: productType,
            product_Type_Id: 0,
            opportunity_Product_Id: 0,
            finance_Partner_Id: 0,
            finance_Partner: '',
            finance_Partner_Display_Name: '',
            finance_Method_Name: '',
            activeFlag: false,
        };
    }
    return {
        product_Type_Name: String(p.Product_Type_Name || p.product_Type_Name || productType),
        product_Type_Id: Number(p.Product_Type_Id ?? p.product_Type_Id ?? 0) || 0,
        opportunity_Product_Id: Number(p.Opportunity_Product_Id ?? p.opportunity_Product_Id ?? 0) || 0,
        finance_Partner_Id: Number(p.Finance_Partner_Id ?? p.FinancePartnerId ?? p.finance_Partner_Id ?? 0) || 0,
        finance_Partner: String(p.Finance_Partner ?? p.finance_Partner ?? ''),
        finance_Partner_Display_Name: String(
            p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.finance_Partner_Display_Name ?? ''
        ),
        finance_Method_Name: String(p.Finance_Method_Name ?? p.FinanceMethod ?? p.finance_Method_Name ?? ''),
        activeFlag: p.ActiveFlag !== false && p.activeFlag !== false,
    };
}

export function matchProductName(p: any, target: string): boolean {
    const n = String(p?.Product_Type_Name ?? p?.ProductType ?? p?.product_Type_Name ?? '').trim();
    if (n.toLowerCase() === target.toLowerCase()) return true;
    if (target === 'RR' && /^(rr|roof\s*replacement)$/i.test(n)) return true;
    if (target === 'RRR' && /rrr/i.test(n)) return true;
    return false;
}

const FINANCE_PLACEHOLDER_RE = /^(select\s+(finance\s+)?partner|select\s+finance|select\s+method)$/i;

function normalizeHubPartnerName(name: string): string {
    const n = name.trim().toLowerCase();
    if (!n || n.includes('select finance')) return '';
    return n;
}

function normalizeHubMethodName(name: string): string {
    const n = name.trim().toLowerCase();
    if (!n || n === 'select method') return '';
    return n;
}

function savedPartnerName(p: any): string {
    return normalizeHubPartnerName(
        String(p?.Finance_Partner_Display_Name ?? p?.FinancePartnerDisplayName ?? p?.Finance_Partner ?? ''),
    );
}

/** True when hub row differs from persisted opportunity product (partner, method, or active flag). */
export function hubRowHasUnsavedChanges(row: HubGridRow, savedArr: unknown[]): boolean {
    const savedList = Array.isArray(savedArr) ? savedArr : [];
    const saved = savedList.find((p: any) => matchProductName(p, row.product_Type_Name));

    const rowActive = !!row.activeFlag;
    const savedActive = !!(saved?.ActiveFlag ?? saved?.activeFlag ?? false);
    if (rowActive !== savedActive) return true;

    if (!rowActive) return false;

    const rowPartner = normalizeHubPartnerName(String(row.finance_Partner_Display_Name ?? ''));
    if (rowPartner !== savedPartnerName(saved)) return true;

    const rowMethod = normalizeHubMethodName(String(row.finance_Method_Name ?? ''));
    const savedMethod = normalizeHubMethodName(
        String(saved?.Finance_Method_Name ?? saved?.FinanceMethod ?? ''),
    );
    if (rowMethod !== savedMethod) return true;

    return false;
}

/** True when Solar finance partner display name differs from persisted opportunity product. */
export function solarFinancePartnerChanged(rows: HubGridRow[], savedArr: unknown[]): boolean {
    const solarRow = rows.find((r) => r.product_Type_Name === 'Solar');
    if (!solarRow) return false;

    const savedList = Array.isArray(savedArr) ? savedArr : [];
    const saved = savedList.find((p: any) => matchProductName(p, 'Solar'));

    const rowPartner = normalizeHubPartnerName(String(solarRow.finance_Partner_Display_Name ?? ''));
    return rowPartner !== savedPartnerName(saved);
}

/** True when a non-placeholder finance partner is persisted for this product (hub matches saved). */
export function hubRowHasSavedFinancePartner(row: HubGridRow | undefined, savedArr: unknown[]): boolean {
    if (!row) return false;

    const displayName = String(row.finance_Partner_Display_Name ?? '').trim();
    if (!displayName || FINANCE_PLACEHOLDER_RE.test(displayName)) return false;

    const savedList = Array.isArray(savedArr) ? savedArr : [];
    const saved = savedList.find((p: any) => matchProductName(p, row.product_Type_Name));
    const persistedPartner = savedPartnerName(saved);
    if (!persistedPartner) return false;

    return !hubRowHasUnsavedChanges(row, savedArr);
}
